import { apiFetch } from './api'

/** Mirrors apps/api/src/backoffice/customers.dto.ts. The only writes are suspend and unsuspend. */

export interface CustomerListItemDto {
  id: string
  mobile: string
  firstName: string | null
  lastName: string | null
  nationalCode: string | null
  isProfileComplete: boolean
  orderCount: number
  policyCount: number
  suspendedAt: string | null
  suspendedReason: string | null
  createdAt: string
}

export interface CustomerPageDto {
  items: CustomerListItemDto[]
  total: number
  page: number
  pageSize: number
}

export interface CustomerProfileDto {
  id: string
  mobile: string
  firstName: string | null
  lastName: string | null
  nationalCode: string | null
  birthDate: string | null
  email: string | null
  isProfileComplete: boolean
  suspendedAt: string | null
  suspendedReason: string | null
  createdAt: string
  updatedAt: string
}

export interface CustomerVehicleDto {
  id: string
  modelLabelFa: string
  groupFa: string
  usageFa: string
  productionYear: number
  plateFa: string
  createdAt: string
}

export interface CustomerInsuredPersonDto {
  id: string
  firstName: string
  lastName: string
  nationalCode: string
  birthDate: string
  passportNo: string | null
  createdAt: string
}

export type OrderStatus =
  | 'DRAFT'
  | 'PENDING_PAYMENT'
  | 'PAYMENT_FAILED'
  | 'PAID'
  | 'ISSUING'
  | 'ISSUED'
  | 'ISSUE_FAILED'
  | 'CANCELLED'

export interface CustomerOrderDto {
  id: string
  status: OrderStatus
  statusFa: string
  amount: number
  productTitleFa: string
  insurerName: string
  policyId: string | null
  createdAt: string
}

export type PolicyStatus = 'UPCOMING' | 'ACTIVE' | 'EXPIRED'

export interface CustomerPolicyDto {
  id: string
  policyNumber: string
  productTitleFa: string
  insurerName: string
  startsAt: string
  endsAt: string
  issuedAt: string
  amount: number
  status: PolicyStatus
  statusFa: string
}

export interface CustomerDetailDto {
  profile: CustomerProfileDto
  counts: { vehicles: number; insuredPersons: number; orders: number; policies: number }
  /** The latest five of each, newest first; `counts` has the totals. */
  recent: {
    vehicles: CustomerVehicleDto[]
    insuredPersons: CustomerInsuredPersonDto[]
    orders: CustomerOrderDto[]
    policies: CustomerPolicyDto[]
  }
}

export const listCustomers = ({
  q,
  suspended,
  page,
}: {
  q: string
  suspended: boolean
  page: number
}) => {
  const params = new URLSearchParams({ page: String(page) })
  if (q) params.set('q', q)
  if (suspended) params.set('suspended', 'true')
  return apiFetch<CustomerPageDto>(`/admin/customers?${params.toString()}`)
}

export const getCustomer = (id: string) =>
  apiFetch<CustomerDetailDto>(`/admin/customers/${encodeURIComponent(id)}`)

/** ADMIN only (`CUSTOMER_SUSPENSION_ROLES`); `409 CUSTOMER_ALREADY_SUSPENDED` if someone was first. */
export const suspendCustomer = (id: string, reason: string) =>
  apiFetch<CustomerProfileDto>(`/admin/customers/${encodeURIComponent(id)}/suspend`, {
    method: 'POST',
    body: { reason },
  })

/** ADMIN only; `409 CUSTOMER_NOT_SUSPENDED` if someone was first. */
export const unsuspendCustomer = (id: string) =>
  apiFetch<CustomerProfileDto>(`/admin/customers/${encodeURIComponent(id)}/unsuspend`, {
    method: 'POST',
  })

/** `علی رضایی`, or null when the customer has not filled in their profile yet. */
export const customerName = (c: {
  firstName: string | null
  lastName: string | null
}): string | null => [c.firstName, c.lastName].filter(Boolean).join(' ') || null
